import searchAtomPair from 'atoms/search-atom';
import { createMutationAtom } from 'atoms/create-resource-atom';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import { ENTITY_TYPE_ITEMS, type EntityType } from 'lib/entities';
import type { EntitySummaryStatsResponse } from 'types/api-responses';
import type {
  EntitySearchParams,
  EntitySearchResponse,
  DateRange,
} from './types';

const parseUrlParams = (): Partial<EntitySearchParams> => {
  const params = new URLSearchParams(window.location.search);

  const entityTypesParam = params.get('entityTypes');
  const entityTypes = entityTypesParam
    ? (entityTypesParam.split(',').filter(Boolean) as EntityType[])
    : undefined;

  const operatorOnly = params.get('operatorOnly') === 'true' || undefined;

  const createdAtStart = params.get('createdAtStart');
  const createdAtEnd = params.get('createdAtEnd');
  const createdAt =
    createdAtStart || createdAtEnd
      ? { start: createdAtStart || undefined, end: createdAtEnd || undefined }
      : undefined;

  const updatedAtStart = params.get('updatedAtStart');
  const updatedAtEnd = params.get('updatedAtEnd');
  const updatedAt =
    updatedAtStart || updatedAtEnd
      ? { start: updatedAtStart || undefined, end: updatedAtEnd || undefined }
      : undefined;

  const text = params.get('text') || undefined;
  // Only default to name/ASC sorting when no search text is present
  // When text is present, let the backend use relevance-based sorting
  const sortAttribute =
    params.get('sortAttribute') || (text ? undefined : 'name');
  const sortDirection =
    (params.get('sortDirection') as 'ASC' | 'DESC' | undefined) ||
    (text ? undefined : 'ASC');

  const includePrimaryParam = params.get('includePrimary');
  const includePrimary =
    includePrimaryParam === 'true'
      ? true
      : includePrimaryParam === 'false'
        ? false
        : undefined;

  const includeAliasesParam = params.get('includeAliases');
  const includeAliases =
    includeAliasesParam === 'true'
      ? true
      : includeAliasesParam === 'false'
        ? false
        : undefined;

  const includeDeduplicatedParam = params.get('includeDeduplicated');
  const includeDeduplicated =
    includeDeduplicatedParam === 'true'
      ? true
      : includeDeduplicatedParam === 'false'
        ? false
        : undefined;

  return {
    page: parseInt(params.get('page') || '1', 10),
    pageSize: parseInt(params.get('pageSize') || String(DEFAULT_PAGE_SIZE), 10),
    text,
    sortAttribute,
    sortDirection,
    entityTypes,
    operatorOnly,
    createdAt,
    updatedAt,
    includePrimary,
    includeAliases,
    includeDeduplicated,
  };
};

export const createEntitySearchAtom = () => {
  const initialParams = parseUrlParams();

  return searchAtomPair<EntitySearchResponse, EntitySearchParams>(
    { href: '/queries/entities' },
    {
      page: initialParams.page || 1,
      pageSize: initialParams.pageSize || DEFAULT_PAGE_SIZE,
      text: initialParams.text,
      sortAttribute: initialParams.sortAttribute,
      sortDirection: initialParams.sortDirection,
      // Filters default to "all visible": every type/classification checked, with
      // Deduplicated the lone exception (hidden unless explicitly opted in).
      entityTypes: initialParams.entityTypes ?? ENTITY_TYPE_ITEMS,
      operatorOnly: initialParams.operatorOnly,
      createdAt: initialParams.createdAt,
      updatedAt: initialParams.updatedAt,
      includePrimary: initialParams.includePrimary ?? true,
      includeAliases: initialParams.includeAliases ?? true,
      includeDeduplicated: initialParams.includeDeduplicated ?? false,
    }
  );
};

export const entitySummaryAtom = createMutationAtom<
  EntitySummaryStatsResponse,
  'entitySummary'
>();

type UrlLocation = { search: string };
type UrlHistory = { push: (location: { search: string }) => void };

const setOrDelete = (
  params: URLSearchParams,
  key: string,
  value: string | undefined
) => {
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
};

const syncToUrl = (
  location: UrlLocation,
  history: UrlHistory,
  updater: (params: URLSearchParams) => void,
  resetPage = true
) => {
  const params = new URLSearchParams(location.search);
  updater(params);
  if (resetPage) params.set('page', '1');
  history.push({ search: params.toString() });
};

const syncDateRangeToUrl = (
  prefix: 'createdAt' | 'updatedAt',
  dateRange: DateRange | undefined,
  location: UrlLocation,
  history: UrlHistory
) => {
  syncToUrl(location, history, (params) => {
    setOrDelete(params, `${prefix}Start`, dateRange?.start);
    setOrDelete(params, `${prefix}End`, dateRange?.end);
  });
};

export const syncCreatedAtToUrl = (
  dateRange: DateRange | undefined,
  location: UrlLocation,
  history: UrlHistory
) => syncDateRangeToUrl('createdAt', dateRange, location, history);

export const syncUpdatedAtToUrl = (
  dateRange: DateRange | undefined,
  location: UrlLocation,
  history: UrlHistory
) => syncDateRangeToUrl('updatedAt', dateRange, location, history);

export const syncEntityTypesToUrl = (
  types: EntityType[],
  location: UrlLocation,
  history: UrlHistory
) => {
  syncToUrl(location, history, (params) => {
    setOrDelete(
      params,
      'entityTypes',
      types.length > 0 ? types.join(',') : undefined
    );
  });
};

export const syncOperatorOnlyToUrl = (
  operatorOnly: boolean,
  location: UrlLocation,
  history: UrlHistory
) => {
  syncToUrl(location, history, (params) => {
    setOrDelete(params, 'operatorOnly', operatorOnly ? 'true' : undefined);
  });
};

export const syncSearchToUrl = (
  text: string | undefined,
  location: UrlLocation,
  history: UrlHistory
) => {
  syncToUrl(location, history, (params) => {
    setOrDelete(params, 'text', text);
    // Clear sorting params when search changes so backend uses relevance-based sorting
    params.delete('sortAttribute');
    params.delete('sortDirection');
  });
};

export const syncSortToUrl = (
  sortAttribute: string | undefined,
  sortDirection: 'ASC' | 'DESC' | undefined,
  location: UrlLocation,
  history: UrlHistory
) => {
  syncToUrl(location, history, (params) => {
    setOrDelete(params, 'sortAttribute', sortAttribute);
    setOrDelete(params, 'sortDirection', sortDirection);
  });
};

export const syncPaginationToUrl = (
  page: number,
  pageSize: number,
  location: UrlLocation,
  history: UrlHistory
) => {
  syncToUrl(
    location,
    history,
    (params) => {
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
    },
    false
  );
};

export const syncEntityTypeFilterToUrl = (
  includePrimary: boolean | undefined,
  includeAliases: boolean | undefined,
  location: UrlLocation,
  history: UrlHistory
) => {
  syncToUrl(location, history, (params) => {
    setOrDelete(
      params,
      'includePrimary',
      includePrimary === undefined ? undefined : String(includePrimary)
    );
    setOrDelete(
      params,
      'includeAliases',
      includeAliases === undefined ? undefined : String(includeAliases)
    );
  });
};

export const syncDeduplicatedToUrl = (
  includeDeduplicated: boolean | undefined,
  location: UrlLocation,
  history: UrlHistory
) => {
  syncToUrl(location, history, (params) => {
    setOrDelete(
      params,
      'includeDeduplicated',
      includeDeduplicated === undefined
        ? undefined
        : String(includeDeduplicated)
    );
  });
};

export const clearFilterParams = (
  location: UrlLocation,
  history: UrlHistory
) => {
  syncToUrl(location, history, (params) => {
    params.delete('createdAtStart');
    params.delete('createdAtEnd');
    params.delete('updatedAtStart');
    params.delete('updatedAtEnd');
    params.delete('entityTypes');
    params.delete('operatorOnly');
    params.delete('includePrimary');
    params.delete('includeAliases');
    params.delete('includeDeduplicated');
  });
};
